// Sidebar.js
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import useIsMobile from './useIsMobile';
import ClickHouseStatus from './ClickHouseStatus';
import './Sidebar.css';

/* ─── Sign-out icon ─── */
function SignOutIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

/* ─── Thin-stroke SVG icons (24x24 viewBox, strokeWidth 1.5) ─── */
export const pageIcons = {
  home: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  fileCode: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="m10 13-2 2 2 2" />
      <path d="m14 13 2 2-2 2" />
    </svg>
  ),
  shield: (
    // shield + key — access control / permissions
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      <circle cx="12" cy="10" r="2" />
      <path d="M12 12v3" />
    </svg>
  ),
};

/* ─── Section icons (smaller, for section headers) ─── */
const sectionIcons = {
  // Home is a link rather than a collapsible group, but it is presented as one
  // of these rows, so it needs a glyph drawn at the section-icon size.
  Home: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  Coaching: (
    // The move a coach draws: a player, and the run he is being sent on. Three strokes — at 13px
    // a fourth is a smudge — and the dot left open so it carries the same weight as the Admin cog.
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <circle cx="5.5" cy="18.5" r="2" />
      <path d="M7.3 17.4C9 13 12 10.5 17 9" />
      <path d="M13.2 8.2 17 9l-1.4 3.6" />
    </svg>
  ),
  Admin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
};

const PAGE_CONFIG = [
  { id: '/', name: 'Home', section: 'top', icon: 'home' },
  // COACHING
  { id: '/tactical-xml', name: 'Tactical XML', section: 'Coaching', icon: 'fileCode' },
  // ADMIN
  { id: '/permissions', name: 'Permissions', section: 'Admin', icon: 'shield', adminOnly: true },
];

const SECTIONS = ['Coaching', 'Admin'];

// Pages the admin can grant/revoke per user (everything except always-open Home
// and the admin-only Permissions page). Shared with the Permissions grid.
export const ACCESS_PAGES = PAGE_CONFIG.filter(
  (p) => p.section !== 'top' && !p.adminOnly
);
export { PAGE_CONFIG, SECTIONS };

/* ─── Accent ring layers for active nav item ─── */
function GoldRing() {
  return (
    <>
      <div className="nav-gold-glow" />
      <div className="nav-gold-clip">
        <div className="nav-gold-gradient" />
      </div>
      <div className="nav-gold-plate" />
    </>
  );
}

/* ─── NavIcon component ─── */
function NavIcon({ name }) {
  return <span className="nav-icon">{pageIcons[name] || pageIcons.home}</span>;
}

/* ─── Glassmorphic Sidebar Tooltip (portal) ─── */
function SidebarTooltip({ text, anchorRef, visible }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!visible || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    setPos({ top: r.top + r.height / 2, left: r.right + 12 });
  }, [visible, anchorRef]);

  if (!visible || !text) return null;
  return ReactDOM.createPortal(
    <div className="sidebar-tooltip" style={{ top: pos.top, left: pos.left }}>
      {text}
    </div>,
    document.body
  );
}

function useSidebarTooltip(isCollapsed) {
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);
  const onEnter = useCallback(() => {
    if (!isCollapsed) return;
    timer.current = setTimeout(() => setShow(true), 300);
  }, [isCollapsed]);
  const onLeave = useCallback(() => { clearTimeout(timer.current); setShow(false); }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return { ref, show, onEnter, onLeave };
}

/* ─── NavItem with tooltip ─── */
function NavItem({ page, active, isCollapsed, children }) {
  const tt = useSidebarTooltip(isCollapsed);
  return (
    <Link
      ref={tt.ref}
      to={page.id}
      className={`nav-item ${active ? 'active' : ''}`}
      onMouseEnter={tt.onEnter}
      onMouseLeave={tt.onLeave}
    >
      {children}
      <SidebarTooltip text={page.name} anchorRef={tt.ref} visible={tt.show} />
    </Link>
  );
}

/* ─── A top-level page presented as a section row ───
 * Home isn't a collapsible group, but it reads as a peer of Coaching /
 * Admin rather than as a page inside one — so it uses
 * the same markup as SectionHeader (same height, tile, uppercase title, and the
 * same centring when collapsed) with a Link in place of the toggle, and no
 * chevron since there is nothing to expand. */
function SectionLink({ page, icon, active, isCollapsed }) {
  const tt = useSidebarTooltip(isCollapsed);
  return (
    <Link
      ref={tt.ref}
      to={page.id}
      className={`section-header section-link ${active ? 'has-active' : ''}`}
      onMouseEnter={tt.onEnter}
      onMouseLeave={tt.onLeave}
    >
      <span className="section-header-icon">{icon}</span>
      <span className="section-title">{page.name}</span>
      <SidebarTooltip text={page.name} anchorRef={tt.ref} visible={tt.show} />
    </Link>
  );
}

/* ─── Chevron icon for collapsible sections ─── */
function ChevronIcon({ expanded }) {
  return (
    <svg
      className={`section-chevron ${expanded ? 'expanded' : ''}`}
      viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      width="10" height="10"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function SectionHeader({ sectionName, icon, hasActive, expanded, onClick, isCollapsed }) {
  const tt = useSidebarTooltip(isCollapsed);
  return (
    <div
      ref={tt.ref}
      className={`section-header ${hasActive ? 'has-active' : ''}`}
      onClick={onClick}
      onMouseEnter={tt.onEnter}
      onMouseLeave={tt.onLeave}
    >
      <span className="section-header-icon">{icon}</span>
      <span className="section-title">{sectionName}</span>
      <ChevronIcon expanded={expanded} />
      <SidebarTooltip text={sectionName} anchorRef={tt.ref} visible={tt.show} />
    </div>
  );
}

function SidebarToggleWithTooltip({ isCollapsed, toggleSidebar, isMobile }) {
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);
  const onEnter = useCallback(() => {
    timer.current = setTimeout(() => setShow(true), 300);
  }, []);
  const onLeave = useCallback(() => { clearTimeout(timer.current); setShow(false); }, []);
  const onClick = useCallback(() => { clearTimeout(timer.current); setShow(false); toggleSidebar(); }, [toggleSidebar]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const label = isMobile ? 'Close' : (isCollapsed ? 'Expand' : 'Collapse');
  return (
    <button ref={ref} className="sidebar-toggle" onClick={onClick} onMouseEnter={onEnter} onMouseLeave={onLeave}
      aria-label={label}>
      {/* In the drawer the same button closes it, so it wears a cross rather than a chevron that
          would point at a rail width nothing on a phone has. */}
      {isMobile ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: isCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s ease' }}>
          <path d="M15 18l-6-6 6-6" />
        </svg>
      )}
      <SidebarTooltip text={label} anchorRef={ref} visible={show} />
    </button>
  );
}

function Sidebar({ isCollapsed, toggleSidebar }) {
  const location = useLocation();
  const { logout, user, can } = useAuth();
  /* ─── The phone drawer ───
   * There is no room for a rail beside the content at 375px, so the sidebar slides in over the
   * page instead and is summoned by a floating button. `isCollapsed` — the desktop rail's
   * expanded/narrow state — is meaningless there: a 64px column of unlabelled icons with no
   * hover tooltips to explain them is a guessing game, so the drawer always renders expanded.
   * Its own open/closed state lives here rather than in main.js because nothing outside the
   * sidebar needs to know about it; the content underneath doesn't move. */
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const collapsed = isMobile ? false : isCollapsed;

  // Tapping a page is the end of the errand — the drawer gets out of the way rather than leaving
  // the reader to dismiss it before they can see what they navigated to.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Leaving phone width with the drawer open would strand a fixed overlay over the desktop layout.
  useEffect(() => { if (!isMobile) setDrawerOpen(false); }, [isMobile]);

  // The page behind a drawer must not scroll under the finger.
  useEffect(() => {
    if (!isMobile || !drawerOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isMobile, drawerOpen]);
  // Nothing is open on arrival. The effect below opens whichever section holds the page you're
  // actually on, so a deep link still reveals itself.
  const [expandedSections, setExpandedSections] = useState({});

  const isActive = (path) => location.pathname === path;

  // Only the pages this user is allowed to open. Memoised on `can`, which only changes with the
  // signed-in user — the sidebar re-renders on every route change, and re-checking every page's
  // grant each time bought nothing.
  const visiblePages = useMemo(() => PAGE_CONFIG.filter(p => can(p.id)), [can]);
  const pagesBySection = useMemo(() => {
    const m = {};
    for (const p of visiblePages) (m[p.section] = m[p.section] || []).push(p);
    return m;
  }, [visiblePages]);

  // Auto-expand section if a page within it is active
  useEffect(() => {
    for (const sectionName of SECTIONS) {
      const sectionPages = PAGE_CONFIG.filter(p => p.section === sectionName);
      if (sectionPages.some(p => location.pathname === p.id)) {
        setExpandedSections(prev => ({ ...prev, [sectionName]: true }));
      }
    }
  }, [location.pathname]);

  const toggleSection = (name) => {
    setExpandedSections(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const topPages = pagesBySection.top || [];
  const displayName = (user && user.displayName) || 'User';
  const initials = displayName.split(/\s+/).filter(Boolean).slice(0, 2)
    .map(w => w.charAt(0).toUpperCase()).join('') || 'U';
  const roleLabel = user && user.isAdmin ? 'Admin' : 'User';

  return (
    <>
      {/* Rendered on every viewport and hidden off-phone by mobile.css, so a window resize can
          never catch the drawer half-built — but it stands down while the drawer is out, which
          covers this corner and carries its own close button. */}
      <button
        type="button"
        className="sidebar-mobile-open"
        onClick={() => setDrawerOpen(true)}
        aria-label="Open menu"
        hidden={isMobile && drawerOpen}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>
      {isMobile && drawerOpen && (
        <div className="sidebar-backdrop" onClick={() => setDrawerOpen(false)} />
      )}
      <div className={`sidebar ${collapsed ? 'collapsed' : ''}${isMobile && drawerOpen ? ' mobile-open' : ''}`}>
        {/* Expand/Collapse toggle inside sidebar top */}
        <SidebarToggleWithTooltip
          isCollapsed={collapsed}
          isMobile={isMobile}
          toggleSidebar={isMobile ? () => setDrawerOpen(false) : toggleSidebar}
        />

        <div className="nav-section">
          {/* Top section (Home only) */}
          <div className="section">
            {topPages.map((page) => (
              <SectionLink
                key={page.id}
                page={page}
                icon={sectionIcons[page.name] || sectionIcons.Home}
                active={isActive(page.id)}
                isCollapsed={collapsed}
              />
            ))}
          </div>

          {SECTIONS.map((sectionName) => {
            const sectionPages = pagesBySection[sectionName] || [];
            if (sectionPages.length === 0) return null; // hide sections with no accessible pages
            const expanded = !!expandedSections[sectionName];
            const hasActive = sectionPages.some(p => isActive(p.id));
            return (
              <div className={`section ${expanded ? 'section-expanded' : ''}`} key={sectionName}>
                <SectionHeader
                  sectionName={sectionName}
                  icon={sectionIcons[sectionName]}
                  hasActive={hasActive}
                  expanded={expanded}
                  onClick={() => toggleSection(sectionName)}
                  isCollapsed={collapsed}
                />
                {expanded && (
                  <div className="section-items">
                    {sectionPages.length === 0 && !collapsed && (
                      <div className="section-empty">No items yet</div>
                    )}
                    {sectionPages.map((page) => {
                      const active = isActive(page.id);
                      return (
                        <NavItem key={page.id} page={page} active={active} isCollapsed={collapsed}>
                          {active && <GoldRing />}
                          <NavIcon name={page.icon} />
                          <span className="nav-label">{page.name}</span>
                        </NavItem>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="sidebar-bottom">
          {/* Divider */}
          <div className="sidebar-divider" />

          {/* User profile */}
          <div className="user-profile">
            <div className="user-avatar-wrap">
              <div className="user-avatar">{initials}</div>
              <ClickHouseStatus />
              <button
                type="button"
                className="signout-badge"
                onClick={logout}
                title="Sign out"
                aria-label="Sign out"
              >
                <SignOutIcon />
              </button>
            </div>
            <div className="user-info">
              <div className="user-name">{displayName}</div>
              <div className="user-role">{roleLabel}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Sidebar;
